"use client";

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  MessageCircle,
  X,
  Award,
  Users,
  BookOpen,
  Star,
  Shield,
  CheckCircle,
  MapPin,
  Phone,
  Mail,
  GraduationCap,
  Building,
  Briefcase,
  Globe,
} from "lucide-react";

export default function RegisterPage() {
  // Initialize form state
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    phone: "",
    course: "",
    experience: "",
    education: "",
    company: "",
    city: "",
    state: "",
    preferredMode: "",
    preferredTime: "",
    hearAboutUs: "",
    message: "",
    agreeTerms: false,
    agreeMarketing: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreeTerms) {
      alert("Please agree to the terms and conditions to proceed.");
      return;
    }
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log("Form submitted:", formData);
    alert("Registration submitted successfully! We'll contact you within 24 hours.");

    // Reset form
    setFormData({
      fullname: "",
      email: "",
      phone: "",
      course: "",
      experience: "",
      education: "",
      company: "",
      city: "",
      state: "",
      preferredMode: "",
      preferredTime: "",
      hearAboutUs: "",
      message: "",
      agreeTerms: false,
      agreeMarketing: false,
    });
    setIsSubmitting(false);
  };

  // Handle chat form submission
  const handleChatSubmit = (e) => {
    e.preventDefault();
    console.log("Chat form submitted");
  };

  // Data definitions
  const courses = [
    {
      value: "etabs",
      label: "ETABS - Advanced Structural Analysis",
      duration: "6 weeks",
      price: "₹15,000",
      level: "Intermediate",
    },
    {
      value: "staadpro",
      label: "STAAD.Pro - Complete Design Package",
      duration: "8 weeks",
      price: "₹18,000",
      level: "Beginner to Advanced",
    },
    {
      value: "autocad",
      label: "AutoCAD - Structural Drafting",
      duration: "4 weeks",
      price: "₹12,000",
      level: "Beginner",
    },
    {
      value: "tekla",
      label: "Tekla Structures - Steel Detailing",
      duration: "10 weeks",
      price: "₹25,000",
      level: "Advanced",
    },
    {
      value: "safe",
      label: "SAFE - Slab & Foundation Design",
      duration: "5 weeks",
      price: "₹14,000",
      level: "Intermediate",
    },
    {
      value: "manual",
      label: "Manual RCC/Steel Design",
      duration: "12 weeks",
      price: "₹20,000",
      level: "Beginner to Advanced",
    },
  ];

  const experienceLevels = [
    { value: "fresher", label: "Fresher (0-1 years)" },
    { value: "junior", label: "Junior (1-3 years)" },
    { value: "mid", label: "Mid-level (3-7 years)" },
    { value: "senior", label: "Senior (7+ years)" },
  ];

  const educationLevels = [
    { value: "diploma", label: "Diploma in Civil Engineering" },
    { value: "btech", label: "B.Tech/B.E. Civil Engineering" },
    { value: "mtech", label: "M.Tech/M.E. Structural Engineering" },
    { value: "other", label: "Other Engineering Degree" },
  ];

  const states = [
    "Tamil Nadu",
    "Karnataka",
    "Kerala",
    "Andhra Pradesh",
    "Telangana",
    "Maharashtra",
    "Gujarat",
    "Rajasthan",
    "Delhi",
    "Other",
  ];

  const footerLinks = [
    { to: "/", label: "HOME" },
    { to: "/psdct", label: "PSDCT LIVE MODE" },
    { to: "/terms", label: "TERM & CONDITIONS" },
    { to: "/quick-payment", label: "QUICK PAY ANY FEES" },
    { to: "/registration-policy", label: "REGISTRATION POLICY" },
    { to: "/cancellation", label: "CANCELLATION POLICY" },
    { to: "/careers", label: "CAREERS" },
    { to: "/contact", label: "CONTACT US" },
    { to: "/franchise", label: "FRANCHISE" },
    { to: "/free", label: "FREE" },
    { to: "/students", label: "STUDENTS" },
    { to: "/training", label: "TRAINING CENTERS" },
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Structural Engineer at L&T",
      content: "The ETABS course helped me advance my career significantly. Excellent training quality!",
      rating: 5,
    },
    {
      name: "Priya Sharma",
      role: "Design Engineer at Tata Projects",
      content: "Professional instructors and hands-on approach. Highly recommended for structural engineers.",
      rating: 5,
    },
    {
      name: "Amit Patel",
      role: "Senior Engineer at Shapoorji Pallonji",
      content: "Best investment in my career. The practical knowledge gained is invaluable.",
      rating: 5,
    },
    {
      name: "Srivani B.N",
      role: "Structural Consultant at Godrej Properties",
      content:
        "Outstanding course content and excellent support. The practical projects really helped me understand real-world applications.",
      rating: 5,
    },
    {
      name: "Gietheshv K.R",
      role: "Senior Design Engineer at Larsen & Toubro",
      content:
        "Comprehensive training with industry-standard practices. The instructors are highly knowledgeable and supportive throughout.",
      rating: 5,
    },
  ];

  // Debugging: Log testimonials to catch potential issues
  useEffect(() => {
    console.log("Testimonials:", testimonials);
    testimonials.forEach((t, i) => {
      console.log(`Testimonial ${i}:`, t.rating, typeof t.rating);
    });
  }, []);

  return (
    <div className="bg-white text-gray-800 min-h-screen overflow-x-hidden">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/20 backdrop-blur-2xl border-b border-white/10 shadow-xl"
            : "bg-gray-900/30 backdrop-blur-xl text-white border-b border-white/5"
        }`}
      >
        <div className="container mx-auto flex justify-between items-center p-4">
          <div
            className={`text-2xl font-bold transition-all duration-500 ${isScrolled ? "text-[#06b6d4]" : "text-white"}`}
          >
            <Link to="/" className="relative inline-block">
              <span className="bg-gradient-to-r from-[#00d4ff] via-[#5b21b6] to-[#ec4899] bg-clip-text text-transparent">
                StructuralGuru
              </span>
              <div className="absolute -inset-3 bg-gradient-to-r from-[#00d4ff]/20 via-[#5b21b6]/20 to-[#ec4899]/20 rounded-2xl blur-2xl opacity-50 animate-pulse"></div>
              <div className="absolute -inset-4 bg-gradient-to-r from-[#00d4ff]/15 via-[#5b21b6]/15 to-[#ec4899]/15 rounded-3xl blur-3xl opacity-30 animate-pulse"></div>
            </Link>
          </div>
          <nav className="hidden md:flex space-x-6">
            <Link
              to="/"
              className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
              } ${
                isScrolled
                  ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                  : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
              } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
            >
              HOME
            </Link>
            <Link
              to="/about"
              className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
              } ${
                isScrolled
                  ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                  : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
              } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
            >
              ABOUT US
            </Link>
            <Link
              to="/services"
              className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
              } ${
                isScrolled
                  ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                  : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
              } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
            >
              SERVICES
            </Link>
            <Link
              to="/projects"
              className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
              } ${
                isScrolled
                  ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                  : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
              } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
            >
              OUR ENGG PROJECTS
            </Link>
            <div className="relative">
              <button
                onClick={() => setIsMoreDropdownOpen(!isMoreDropdownOpen)}
                className={`flex items-center space-x-1 font-medium transition-all duration-300 hover:scale-105 ${
                  isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
                } ${
                  isScrolled
                    ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                    : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
                } rounded-lg px-2 py-1`}
                aria-expanded={isMoreDropdownOpen}
                aria-haspopup="true"
              >
                <span>MORE</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${isMoreDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`absolute top-full right-0 mt-2 w- להת bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/20 overflow-hidden transition-all duration-300 ${
                  isMoreDropdownOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-4 invisible"
                }`}
              >
                <Link
                  to="/privacy"
                  className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                  onClick={() => setIsMoreDropdownOpen(false)}
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/quality"
                  className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                  onClick={() => setIsMoreDropdownOpen(false)}
                >
                  Quality Policy
                </Link>
              </div>
            </div>
          </nav>
          {/* Mobile menu button */}
          <button className="md:hidden text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 bg-gray-50 relative overflow-hidden pt-20">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-[#06b6d4]/30 to-[#8b5cf6]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-l from-[#7c3aed]/25 to-[#06b6d4]/15 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute top-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-[#f59e0b]/20 to-[#ef4444]/15 rounded-full blur-2xl animate-bounce"
            style={{ animationDuration: "6s" }}
          ></div>
          <div
            className="absolute bottom-1/3 left-1/4 w-48 h-48 bg-gradient-to-tr from-[#10b981]/25 to-[#3b82f6]/20 rounded-full blur-2xl animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>

        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center py-12 relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#0f766e] via-[#7c3aed] to-[#0f766e] bg-clip-text text-transparent">
              Professional Structural Engineering Training
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Join India's leading structural engineering institute. Get industry-ready skills with hands-on training from
            experienced professionals.
          </p>

          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-white/20">
              <div className="flex items-center justify-center mb-2">
                <Users className="w-8 h-8 text-[#0f766e]" />
              </div>
              <div className="text-2xl font-bold text-[#0f766e]">5000+</div>
              <div className="text-sm text-gray-600">Students Trained</div>
            </div>
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-white/20">
              <div className="flex items-center justify-center mb-2">
                <Award className="w-8 h-8 text-[#7c3aed]" />
              </div>
              <div className="text-2xl font-bold text-[#7c3aed]">30+</div>
              <div className="text-sm text-gray-600">Years Experience</div>
            </div>
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-white/20">
              <div className="flex items-center justify-center mb-2">
                <Building className="w-8 h-8 text-[#06b6d4]" />
              </div>
              <div className="text-2xl font-bold text-[#06b6d4]">95%</div>
              <div className="text-sm text-gray-600">Placement Rate</div>
            </div>
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-white/20">
              <div className="flex items-center justify-center mb-2">
                <Globe className="w-8 h-8 text-[#0f766e]" />
              </div>
              <div className="text-2xl font-bold text-[#0f766e]">50+</div>
              <div className="text-sm text-gray-600">Cities Covered</div>
            </div>
          </div>

          {/* Certifications */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <span className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm border border-[#0f766e]/30 rounded-full text-sm font-medium">
              <Shield className="w-4 h-4 mr-2" />
              ISO 9001:2015 Certified
            </span>
            <span className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm border border-[#7c3aed]/30 rounded-full text-sm font-medium">
              <Award className="w-4 h-4 mr-2" />
              Best Teaching Awardee
            </span>
            <span className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm border border-[#06b6d4]/30 rounded-full text-sm font-medium">
              <GraduationCap className="w-4 h-4 mr-2" />
              Industry Recognized
            </span>
          </div>
        </section>

        {/* Registration Form */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white/60 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/20 relative">
              <h2 className="text-3xl font-bold mb-6 text-center">
                <span className="bg-gradient-to-r from-[#0f766e] via-[#7c3aed] to-[#0f766e] bg-clip-text text-transparent">
                  Student Registration Form
                </span>
              </h2>

              {/* Progress Indicator */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Registration Progress</span>
                  <span className="text-sm font-medium text-[#0f766e]">
                    {Math.round(
                      (Object.values(formData).filter((v) => v !== "" && v !== false).length /
                        Object.keys(formData).length) *
                        100
                    )}
                    %
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        (Object.values(formData).filter((v) => v !== "" && v !== false).length /
                          Object.keys(formData).length) *
                        100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-[#0f766e]" />
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="fullname" className="block font-semibold text-gray-800 text-sm">
                        Full Name *
                      </label>
                      <input
                        id="fullname"
                        type="text"
                        value={formData.fullname}
                        onChange={(e) => handleInputChange("fullname", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="block font-semibold text-gray-800 text-sm">
                        Email Address *
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                        placeholder="your.email@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="phone" className="block font-semibold text-gray-800 text-sm">
                        Phone Number *
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="city" className="block font-semibold text-gray-800 text-sm">
                        City *
                      </label>
                      <input
                        id="city"
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                        placeholder="Your city"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label htmlFor="state" className="block font-semibold text-gray-800 text-sm">
                        State *
                      </label>
                      <select
                        id="state"
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                      >
                        <option value="">Select your state</option>
                        {Array.isArray(states) &&
                          states.map((state) => (
                            <option key={state} value={state.toLowerCase().replace(" ", "-")}>
                              {state}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <Briefcase className="w-5 h-5 mr-2 text-[#7c3aed]" />
                    Professional Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="education" className="block font-semibold text-gray-800 text-sm">
                        Education Level *
                      </label>
                      <select
                        id="education"
                        value={formData.education}
                        onChange={(e) => handleInputChange("education", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                      >
                        <option value="">Select your education</option>
                        {Array.isArray(educationLevels) &&
                          educationLevels.map((edu) => (
                            <option key={edu.value} value={edu.value}>
                              {edu.label}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="experience" className="block font-semibold text-gray-800 text-sm">
                        Experience Level *
                      </label>
                      <select
                        id="experience"
                        value={formData.experience}
                        onChange={(e) => handleInputChange("experience", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                      >
                        <option value="">Select your experience</option>
                        {Array.isArray(experienceLevels) &&
                          experienceLevels.map((exp) => (
                            <option key={exp.value} value={exp.value}>
                              {exp.label}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label htmlFor="company" className="block font-semibold text-gray-800 text-sm">
                        Current Company/Organization
                      </label>
                      <input
                        id="company"
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange("company", e.target.value)}
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                        placeholder="Company name (if applicable)"
                      />
                    </div>
                  </div>
                </div>

                {/* Course Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <BookOpen className="w-5 h-5 mr-2 text-[#06b6d4]" />
                    Course Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 md:col-span-2">
                      <label htmlFor="course" className="block font-semibold text-gray-800 text-sm">
                        Interested Course *
                      </label>
                      <select
                        id="course"
                        value={formData.course}
                        onChange={(e) => handleInputChange("course", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                      >
                        <option value="">Select your preferred course</option>
                        {Array.isArray(courses) &&
                          courses.map((course) => (
                            <option key={course.value} value={course.value}>
                              {course.label} - {course.duration} • {course.price} • {course.level}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="preferredMode" className="block font-semibold text-gray-800 text-sm">
                        Preferred Mode *
                      </label>
                      <select
                        id="preferredMode"
                        value={formData.preferredMode}
                        onChange={(e) => handleInputChange("preferredMode", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                      >
                        <option value="">Select mode</option>
                        <option value="online">Online Training</option>
                        <option value="offline">Offline Training</option>
                        <option value="hybrid">Hybrid (Online + Offline)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="preferredTime" className="block font-semibold text-gray-800 text-sm">
                        Preferred Time *
                      </label>
                      <select
                        id="preferredTime"
                        value={formData.preferredTime}
                        onChange={(e) => handleInputChange("preferredTime", e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                      >
                        <option value="">Select time</option>
                        <option value="morning">Morning (9 AM - 12 PM)</option>
                        <option value="afternoon">Afternoon (2 PM - 5 PM)</option>
                        <option value="evening">Evening (6 PM - 9 PM)</option>
                        <option value="weekend">Weekends Only</option>
                      </select>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label htmlFor="hearAboutUs" className="block font-semibold text-gray-800 text-sm">
                        How did you hear about us?
                      </label>
                      <select
                        id="hearAboutUs"
                        value={formData.hearAboutUs}
                        onChange={(e) => handleInputChange("hearAboutUs", e.target.value)}
                        className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg"
                      >
                        <option value="">Select source</option>
                        <option value="google">Google Search</option>
                        <option value="social-media">Social Media</option>
                        <option value="referral">Friend/Colleague Referral</option>
                        <option value="advertisement">Advertisement</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="block font-semibold text-gray-800 text-sm">
                    Additional Message or Query
                  </label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange("message", e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 bg-white/40 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 hover:bg-white/60 hover:shadow-lg resize-none"
                    placeholder="Any specific questions or requirements..."
                  />
                </div>

                {/* Terms and Conditions */}
                <div className="space-y-4">
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={(e) => handleInputChange("agreeTerms", e.target.checked)}
                      className="mt-1 w-4 h-4 text-[#0f766e] bg-gray-100 border-gray-300 rounded focus:ring-[#0f766e] focus:ring-2"
                    />
                    <label htmlFor="agreeTerms" className="text-sm text-gray-600 leading-relaxed">
                      I agree to the{" "}
                      <Link to="/terms" className="text-[#0f766e] hover:underline">
                        Terms and Conditions
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy" className="text-[#0f766e] hover:underline">
                        Privacy Policy
                      </Link>{" "}
                      *
                    </label>
                  </div>
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="agreeMarketing"
                      checked={formData.agreeMarketing}
                      onChange={(e) => handleInputChange("agreeMarketing", e.target.checked)}
                      className="mt-1 w-4 h-4 text-[#0f766e] bg-gray-100 border-gray-300 rounded focus:ring-[#0f766e] focus:ring-2"
                    />
                    <label htmlFor="agreeMarketing" className="text-sm text-gray-600 leading-relaxed">
                      I agree to receive marketing communications and course updates via email and SMS
                    </label>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.agreeTerms}
                    className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] hover:from-[#06b6d4] hover:to-[#8b5cf6] text-white px-8 py-3 text-lg transition-all duration-300 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] rounded-2xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      "Submit Registration"
                    )}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">We'll contact you within 24 hours</p>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="bg-white/60 backdrop-blur-xl border border-white/20 shadow-lg rounded-2xl">
              <div className="p-6 border-b border-gray-200/20">
                <h3 className="text-lg font-semibold">
                  <span className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] bg-clip-text text-transparent">
                    Contact Information
                  </span>
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-[#0f766e]" />
                  <div>
                    <p className="font-medium">+91 9629121300</p>
                    <p className="text-sm text-gray-600">Call us anytime</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-[#7c3aed]" />
                  <div>
                    <p className="font-medium">structuralguruofficial@gmail.com</p>
                    <p className="text-sm text-gray-600">Email support</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-[#06b6d4]" />
                  <div>
                    <p className="font-medium">Trichy, Tamil Nadu</p>
                    <p className="text-sm text-gray-600">Head Office</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Features */}
            <div className="bg-white/60 backdrop-blur-xl border border-white/20 shadow-lg rounded-2xl">
              <div className="p-6 border-b border-gray-200/20">
                <h3 className="text-lg font-semibold">
                  <span className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] bg-clip-text text-transparent">
                    Why Choose Us?
                  </span>
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Industry Expert Instructors</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Hands-on Project Training</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Job Placement Assistance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Life Time Guidances</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Industry Certification</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">24/7 Technical Support</span>
                </div>
              </div>
            </div>

            {/* Testimonials */}
            <div className="bg-white/60 backdrop-blur-xl border border-white/20 shadow-lg rounded-2xl">
              <div className="p-6 border-b border-gray-200/20">
                <h3 className="text-lg font-semibold">
                  <span className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] bg-clip-text text-transparent">
                    Student Reviews
                  </span>
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {Array.isArray(testimonials) ? (
                  testimonials.map((testimonial, index) => (
                    <div key={index} className="border-l-4 border-[#0f766e]/30 pl-4">
                      <div className="flex items-center space-x-1 mb-1">
                        {typeof testimonial.rating === "number" && testimonial.rating > 0 ? (
                          Array.from({ length: testimonial.rating }, (_, i) => (
                            <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">No rating available</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">"{testimonial.content}"</p>
                      <div>
                        <p className="font-medium text-sm">{testimonial.name}</p>
                        <p className="text-xs text-gray-500">{testimonial.role}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500">No testimonials available</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white text-sm py-10 relative overflow-hidden">
        <div className="relative z-10">
          <div className="container mx-auto">
            <div className="flex flex-wrap justify-center gap-4 text-center mb-6">
              {Array.isArray(footerLinks) &&
                footerLinks.map((link, index) => (
                  <Link
                    key={index}
                    to={link.to}
                    className="px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gradient-to-r hover:from-[#06b6d4] hover:to-[#7c3aed] transition-all duration-300 border border-gray-600 rounded-lg hover:border-[#06b6d4] hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] relative overflow-hidden group transform hover:scale-105 hover:-translate-y-1"
                  >
                    <span className="relative z-10">{link.label}</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#06b6d4]/20 to-[#7c3aed]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#06b6d4] group-hover:w-full transition-all duration-300"></div>
                  </Link>
                ))}
            </div>
            <div className="text-center">
              <p className="text-gray-400 mb-2">
                © 2025 StructuralGuru - GURURAYAR ASSOCIATES. All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Chat Widget */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="group bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white p-4 rounded-full shadow-2xl hover:shadow-[0_0_25px_rgba(6,182,212,0.6)] transition-all duration-300 hover:scale-110 hover:-translate-y-1 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#06b6d4]/30 to-[#8b5cf6]/30 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300"></div>
          <div className="relative z-10">
            {isChatOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
          </div>
        </button>
      </div>

      {/* Chat Box */}
      <div
        className={`fixed bottom-24 right-6 w-80 bg-white/95 backdrop-blur-xl border border-gray-200/20 rounded-3xl shadow-2xl transition-all duration-500 z-40 ${
          isChatOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 translate-y-8 invisible"
        }`}
      >
        <div className="p-6">
          <h3 className="text-xl font-semibold mb-6 text-center">
            <span className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] bg-clip-text text-transparent">
              Customer Support
            </span>
          </h3>
          <form onSubmit={handleChatSubmit} className="space-y-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300"
                required
              />
            </div>
            <div className="relative">
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300"
                required
              />
            </div>
            <div className="relative">
              <textarea
                placeholder="How can I help you?"
                rows={3}
                className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 resize-none"
                required
              ></textarea>
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white py-3 rounded-2xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(6,182,212,0.5)]"
            >
              Customer Care for Support
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
